/**
 * The page's ground: a slow parallax grid behind the content.
 *
 * The one place a background may animate freely — it holds nothing. Even so it
 * stops under reduced motion, because a drifting field behind text is exactly
 * what that setting is asked for.
 *
 * `translate3d` on a `position: fixed` layer, driven from a scroll listener
 * writing a custom property: no layout, no paint of the content above it.
 */
import { useEffect, useRef } from 'react'

import { useReducedMotionSafe } from './motion'

export function GridDrift({ rate = 0.06 }: { rate?: number }) {
  const reduced = useReducedMotionSafe()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (reduced) return
    let frame = 0
    const apply = () => {
      frame = 0
      ref.current?.style.setProperty('--kl-drift', `${-window.scrollY * rate}px`)
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(apply)
    }
    apply()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [rate, reduced])

  return <div ref={ref} className="kl-griddrift" aria-hidden="true" />
}
